import { en, type MessageKey } from './en';
import { gu } from './gu';
import { hi } from './hi';
import { mr } from './mr';

export type { MessageKey } from './en';

export type Lang = 'en' | 'hi' | 'gu' | 'mr';

export const LANGUAGES: readonly { code: Lang; nativeName: string }[] = [
  { code: 'en', nativeName: 'English' },
  { code: 'hi', nativeName: 'हिन्दी' },
  { code: 'gu', nativeName: 'ગુજરાતી' },
  { code: 'mr', nativeName: 'मराठी' },
];

const BUNDLES: Record<Lang, Partial<Record<MessageKey, string>>> = { en, hi, gu, mr };

export type TranslateVars = Record<string, string | number>;
export type Translate = (key: MessageKey, vars?: TranslateVars) => string;

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && value in BUNDLES;
}

/** Picks the best supported language from a list of BCP-47 tags (e.g. navigator.languages). */
export function detectLang(preferred: readonly string[]): Lang {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return 'en';
}

export function createTranslator(lang: Lang): Translate {
  const bundle = BUNDLES[lang];
  return (key, vars) => {
    const template = bundle[key] ?? en[key] ?? key;
    if (!vars) return template;
    return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(vars[name] ?? ''));
  };
}

/** Keys missing from a language bundle (used by tests and the translation workflow). */
export function missingKeys(lang: Lang): MessageKey[] {
  const bundle = BUNDLES[lang];
  return (Object.keys(en) as MessageKey[]).filter((key) => bundle[key] === undefined);
}
